import { responseStatus } from '../util/Fetch';

export default class CompanyDetails {
  /**
   * Companies details popups
   *
   * @param {string} restUrl  The rest url of the company
   * @param {HTMLElement} detailsElt  The company details element
   */
  constructor(restUrl, detailsElt) {
    /**
     * @property {string} restUrl  The rest url of the company
     */
    this.restUrl = restUrl;

    /**
     * @property {HTMLElement} detailsElt  The company details element
     */
    this.detailsElt = detailsElt;

    /**
     * @property {HTMLElement} headerElt  The company details header element
     */
    this.headerElt = this.detailsElt.querySelector('.company-details-header');

    /**
     * @property {HTMLElement} mainElt  The company details main element
     */
    this.mainElt = this.detailsElt.querySelector('.company-details-main');

    /**
     * @property {HTMLElement} footerElt  The company details footer element
     */
    this.footerElt = this.detailsElt.querySelector('.company-details-footer');

    /**
     * @property {number} transitionDuration  The .company-details-content CSS transition duration
     */
    this.transitionDuration = 500;

    /** Bind the events */
    this.bindEvents();

    /** Initialize */
    this.initialize();
  }

  /**
   * Bind the events
   */
  bindEvents() {
    this.onClickOutsideBox = this.hide.bind(this);
    this.onClickClose = this.hide.bind(this);
  }

  /**
   * Initialize
   */
  initialize() {
    /** Add the protocol to the rest url for local queries */
    if (document.querySelector('html').classList.contains('dev') && this.restUrl.indexOf('http') < 0) {
      this.restUrl = `http:${this.restUrl}`;
    }

    /** Get the company data */
    this.getCompanyData();
  }

  /**
   * Get the company data from the rest api
   *
   * Then clear and populate the company details
   */
  getCompanyData() {
    /** Fetch the results */
    fetch(new URL(this.restUrl), {
      method: 'GET',
    })
      .then(responseStatus)
      .then(response => response.json())
      .then(response => {
        this.clear();
        this.populate(response);
      })
      .catch(error => console.warn(error.message));
  }

  /**
   * Clear all fillable elements
   */
  clear() {
    for (const elt of this.detailsElt.querySelectorAll('[data-fillable]')) {
      elt.innerHTML = '';
    }
  }

  /**
   * Populate the company details with the response from the rest api
   *
   * @param {Object} response
   */
  populate(response) {
    /** Affiliation text and logo */
    const affiliationText = this.detailsElt.getAttribute(`data-txt-${response.fields.affiliation}`);
    const affiliationLogo = this.detailsElt.getAttribute(`data-logo-${response.fields.affiliation}`);

    /** Header */
    this.headerElt.appendChild(this.renderDetailsLogo(response));
    if (Object.keys(response.fields.networks).length > 0) {
      this.headerElt.appendChild(this.renderDetailsNetworks(response));
    }

    /** Main */
    this.mainElt.appendChild(this.renderDetailsName(response));
    this.mainElt.appendChild(this.renderDetailsAffiliation(affiliationText));
    this.mainElt.appendChild(this.renderDetailsText(response));

    /** Footer */
    this.footerElt.appendChild(this.renderDetailsLinks(response, affiliationText, affiliationLogo));

    /** Show the details box */
    this.show();
  }

  /**
   * Render the details-logo element
   *
   * @param {Object} response
   * @returns {HTMLElement}
   */
  renderDetailsLogo(response) {
    const detailsLogo = document.createElement('div');
    let logo = null;
    detailsLogo.className = 'details-logo';
    if (response.fields.logo[0] !== undefined) {
      logo = document.createElement('img');
      logo.src = response.fields.logo[0];
      logo.alt = response.title.rendered;
    } else {
      logo = document.createElement('p');
      logo.innerHTML = response.title.rendered;
    }
    detailsLogo.appendChild(logo);
    return detailsLogo;
  }

  /**
   * Render the details-network element
   *
   * @param {Object} response
   * @returns {HTMLElement}
   */
  renderDetailsNetworks(response) {
    const detailsNetworks = document.createElement('div');
    detailsNetworks.className = 'details-networks';
    const networksList = document.createElement('ul');
    Object.entries(response.fields.networks).forEach(([network, url]) => {
      if (url !== null && url.length > 0) {
        const networksListItem = document.createElement('li');
        const networksListLink = document.createElement('a');
        networksListLink.href = url;
        networksListLink.target = '_blank';
        const networksListIcon = document.createElement('span');
        networksListIcon.className = `icon icon-${network} round-icon`;
        networksListLink.appendChild(networksListIcon);
        networksListItem.appendChild(networksListLink);
        networksList.appendChild(networksListItem);
      }
    });
    detailsNetworks.appendChild(networksList);
    return detailsNetworks;
  }

  /**
   * Render the details-name element
   *
   * @param {Object} response
   * @returns {HTMLElement}
   */
  renderDetailsName(response) {
    const detailsName = document.createElement('div');
    detailsName.className = 'details-name';
    const name = document.createElement('h3');
    name.innerHTML = response.title.rendered;
    detailsName.appendChild(name);
    return detailsName;
  }

  /**
   * Render the details-affiliation element
   *
   * @param {String} affiliationText
   * @returns {HTMLElement}
   */
  renderDetailsAffiliation(affiliationText) {
    const detailsAffiliation = document.createElement('div');
    detailsAffiliation.className = 'details-affiliation';
    const affiliation = document.createElement('p');
    affiliation.innerHTML = `(${affiliationText})`;
    detailsAffiliation.appendChild(affiliation);
    return detailsAffiliation;
  }

  /**
   * Render the details-text element
   *
   * @param {Object} response
   * @returns {HTMLElement}
   */
  renderDetailsText(response) {
    const detailsText = document.createElement('div');
    detailsText.className = 'details-text';
    const labelFor = document.createElement('p');
    const labelForTitle = document.createElement('strong');
    labelForTitle.innerHTML = `${this.detailsElt.getAttribute('data-txt-labellise_pour')} : `;
    const labelForText = document.createElement('span');
    labelForText.innerHTML = response.fields.categories;
    labelFor.appendChild(labelForTitle);
    labelFor.appendChild(document.createElement('br'));
    labelFor.appendChild(labelForText);
    detailsText.appendChild(labelFor);

    const products = document.createElement('div');
    var lang = document.getElementsByTagName('html')[0].getAttribute('lang');

    if (lang == 'fr-FR') {
      products.innerHTML = response.fields.produits_services;
    }
    if (lang == 'de') {
      products.innerHTML = response.acf.entreprise_produits_services_DE;
    }

    if (lang == 'en-GB') {
      products.innerHTML = response.acf.entreprise_produits_services_en;
    }

    detailsText.appendChild(products);

    if (response.fields.site_internet !== null && response.fields.site_internet.length > 0) {
      const website = document.createElement('p');
      const websiteLink = document.createElement('a');
      websiteLink.href =
        response.fields.site_internet.slice(0, 4) !== 'http'
          ? `http://${response.fields.site_internet}`
          : response.fields.site_internet;
      websiteLink.target = '_blank';
      websiteLink.innerHTML = response.fields.site_internet.replace('http://', '').replace('https://', '');
      website.appendChild(websiteLink);
      detailsText.appendChild(website);
    }
    return detailsText;
  }

  /**
   * Render the details-links element
   *
   * @param {Object} response
   * @param {String} affiliationText
   * @param {String} affiliationLogo
   * @returns {HTMLElement}
   */
  renderDetailsLinks(response, affiliationText, affiliationLogo) {
    const detailsLinks = document.createElement('ul');
    detailsLinks.className = 'details-links';

    /** Affiliation */
    const affiliationItem = document.createElement('li');
    const affiliationLink = document.createElement('a');
    affiliationLink.href = this.detailsElt.getAttribute(`data-link-${response.fields.affiliation}`);
    affiliationLink.target = '_blank';
    const affiliationImage = document.createElement('img');
    affiliationImage.src = affiliationLogo;
    affiliationImage.alt = affiliationText;
    affiliationLink.appendChild(affiliationImage);
    affiliationItem.appendChild(affiliationLink);
    detailsLinks.appendChild(affiliationItem);

    /** Ecommerce */
    if (response.fields.site_ecommerce !== null && response.fields.site_ecommerce.length > 0) {
      const shopItem = document.createElement('li');
      const shopLink = document.createElement('a');
      shopLink.href = response.fields.site_ecommerce;
      shopLink.target = '_blank';
      const shopImage = document.createElement('img');
      shopImage.src = this.detailsElt.getAttribute('data-logo-letzshop');
      shopImage.alt = this.detailsElt.getAttribute('data-txt-letzshop');
      shopLink.appendChild(shopImage);
      shopItem.appendChild(shopLink);
      detailsLinks.appendChild(shopItem);
    }
    return detailsLinks;
  }

  /**
   * Show the details box with animation
   */
  show() {
    /** Bind the close button */
    const button = this.detailsElt.querySelector('.button');
    button.addEventListener('click', this.onClickClose);
    button.setAttribute('aria-expanded', 'true');

    /** Hide the details box on click outside it */
    /**
     * ! This actually closes the box even if the click is inside it and must be adapted before being used
     */
    // this.detailsElt.addEventListener('click', this.onClickOutsideBox);

    /** Remove the body scrollbar */
    document.querySelector('body').style.overflow = 'hidden';

    /** Show the box */
    this.detailsElt.removeAttribute('hidden');
    setTimeout(() => {
      this.detailsElt.classList.remove('is-hidden');
    }, 10);
  }

  /**
   * Close the details box with animation
   */
  hide(e) {
    /** Unbind the close button */
    const button = this.detailsElt.querySelector('.button');
    button.removeEventListener('click', this.onClickClose);
    button.setAttribute('aria-expanded', 'false');

    console.info(e.currentTarget);

    /** Unbind the click outside event */
    // this.detailsElt.removeEventListener('click', this.onClickOutsideBox);

    /** Restore the body scrollbar */
    document.querySelector('body').style.overflow = 'visible';

    /** Hide the box */
    this.detailsElt.classList.add('is-hidden');
    setTimeout(() => {
      this.detailsElt.setAttribute('hidden', 'true');
    }, this.transitionDuration);

    /** Dispatch a close event */
    this.detailsElt.dispatchEvent(new Event('close'));
  }
}


// WEBPACK FOOTER //
// ./scripts/components/CompanyDetails.js